
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface Visa {
  id: string;
  visa_type_id: string;
  client_name: string;
  passport_number: string;
  status: string;
  issue_date: string;
  expiry_date: string;
  notes: string;
  created_at: string;
  updated_at: string;
}

interface VisaType {
  id: string;
  name_ar: string;
  name_en: string;
}

interface VisaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  visa?: Visa | null;
  onClose: () => void;
}

export function VisaDialog({ open, onOpenChange, visa, onClose }: VisaDialogProps) {
  const [loading, setLoading] = useState(false);
  const [visaTypes, setVisaTypes] = useState<VisaType[]>([]);
  const [formData, setFormData] = useState({
    visa_type_id: '',
    client_name: '',
    passport_number: '',
    status: 'pending',
    issue_date: '',
    expiry_date: '',
    notes: '',
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchVisaTypes();
  }, []);

  useEffect(() => {
    if (visa) {
      setFormData({
        visa_type_id: visa.visa_type_id,
        client_name: visa.client_name,
        passport_number: visa.passport_number || '',
        status: visa.status,
        issue_date: visa.issue_date || '',
        expiry_date: visa.expiry_date || '',
        notes: visa.notes || '',
      });
    } else {
      setFormData({
        visa_type_id: '',
        client_name: '',
        passport_number: '',
        status: 'pending',
        issue_date: '',
        expiry_date: '',
        notes: '',
      });
    }
  }, [visa]);

  const fetchVisaTypes = async () => {
    try {
      const { data, error } = await supabase
        .from('visa_types')
        .select('*')
        .order('name_ar');

      if (error) throw error;
      setVisaTypes(data || []);
    } catch (error) {
      console.error('Error fetching visa types:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (visa) {
        // تحديث تأشيرة موجودة
        const { error } = await supabase
          .from('visas')
          .update({
            visa_type_id: formData.visa_type_id,
            client_name: formData.client_name,
            passport_number: formData.passport_number,
            status: formData.status,
            issue_date: formData.issue_date || null,
            expiry_date: formData.expiry_date || null,
            notes: formData.notes,
            updated_at: new Date().toISOString(),
          })
          .eq('id', visa.id);

        if (error) throw error;

        toast({
          title: "تم التحديث",
          description: "تم تحديث التأشيرة بنجاح",
        });
      } else {
        // إضافة تأشيرة جديدة
        const { error } = await supabase
          .from('visas')
          .insert([{
            visa_type_id: formData.visa_type_id,
            client_name: formData.client_name,
            passport_number: formData.passport_number,
            status: formData.status,
            issue_date: formData.issue_date || null,
            expiry_date: formData.expiry_date || null,
            notes: formData.notes,
          }]);

        if (error) throw error;

        toast({
          title: "تم الإضافة",
          description: "تم إضافة التأشيرة بنجاح",
        });
      }

      onClose();
    } catch (error) {
      console.error('Error saving visa:', error);
      toast({
        title: "خطأ",
        description: "فشل في حفظ التأشيرة",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {visa ? 'تعديل التأشيرة' : 'إضافة تأشيرة جديدة'}
          </DialogTitle>
          <DialogDescription>
            {visa 
              ? 'قم بتعديل بيانات التأشيرة' 
              : 'أدخل بيانات التأشيرة الجديدة'
            }
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="visa_type_id" required>نوع التأشيرة</Label>
              <Select
                value={formData.visa_type_id}
                onValueChange={(value) => setFormData({ ...formData, visa_type_id: value })}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="اختر نوع التأشيرة" />
                </SelectTrigger>
                <SelectContent>
                  {visaTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name_ar}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="client_name" required>اسم العميل</Label>
              <Input
                id="client_name"
                value={formData.client_name}
                onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                placeholder="اسم العميل"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="passport_number">رقم الجواز</Label>
              <Input
                id="passport_number"
                value={formData.passport_number}
                onChange={(e) => setFormData({ ...formData, passport_number: e.target.value })}
                placeholder="رقم الجواز"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="status">الحالة</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">قيد المراجعة</SelectItem>
                  <SelectItem value="approved">مقبولة</SelectItem>
                  <SelectItem value="rejected">مرفوضة</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="issue_date">تاريخ الإصدار</Label>
                <Input
                  id="issue_date"
                  type="date"
                  value={formData.issue_date}
                  onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="expiry_date">تاريخ الانتهاء</Label>
                <Input
                  id="expiry_date"
                  type="date"
                  value={formData.expiry_date}
                  onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="notes">ملاحظات</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="ملاحظات إضافية"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              إلغاء
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'جاري الحفظ...' : (visa ? 'تحديث' : 'إضافة')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
